import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import AuctionDetailPage from '@features/admin/auctions/AuctionDetailPage.tsx';
import { useAdminAuctionDetailStore } from '@features/admin/auctions/useAdminAuctionDetailStore.ts';

const paramsSchema = z.object({
  auctionId: z.coerce.number().positive(),
});

const searchSchema = z.object({
  // Aktivní tab
  tab: z.enum(['suppliers', 'products', 'prices', 'customers']).optional().default('suppliers'),
  // Stránkování
  page: z.coerce.number().optional().default(1),
  size: z.coerce.number().optional().default(20),
  // Filtry
  q: z.string().optional(),
  supplierId: z.coerce.number().optional(),
  customerId: z.coerce.number().optional(),
  // Filtry pro ceny
  productQuery: z.string().optional(),
  priceType: z.enum(['STANDARD', 'ACTION_PRICE', 'SUBSTITUTE_PRICE', 'FROM_DL_PRICE']).optional(),
  // Pozor na z.coerce.boolean() - ten by ze stringu "false" udelal true, takze by se
  // rucne poslany odkaz s ?guaranteed=false choval jako filtr na garantovane.
  guaranteed: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform(v => v === true || v === 'true')
    .optional(),
});

export const Route = createFileRoute('/admin/auctions/$auctionId')({
  params: {
    parse: v => paramsSchema.parse(v),
  },
  validateSearch: search => searchSchema.parse(search),
  component: RouteComponent,
  beforeLoad: async ({ params, search }) => {
    const auctionId = paramsSchema.parse(params).auctionId;
    const store = useAdminAuctionDetailStore.getState();

    // Načti základní detail aukce
    await store.loadAuctionDetail(auctionId);

    // Načti všechny záložky paralelně - aktivní tab s parametry z URL, ostatní s výchozími hodnotami
    const { tab, page, size, q, supplierId, customerId, productQuery, priceType, guaranteed } = search;

    await Promise.all([
      store.loadSuppliers(auctionId, tab === 'suppliers' ? { page, size, q } : { page: 1, size: 20 }),
      store.loadProducts(auctionId, tab === 'products' ? { page, size, q, supplierId } : { page: 1, size: 20 }),
      store.loadPrices(
        auctionId,
        tab === 'prices'
          ? {
              page,
              size,
              supplierId,
              customerId,
              productQuery,
              priceType,
              guaranteed,
            }
          : { page: 1, size: 20 },
      ),
      store.loadCustomers(auctionId, tab === 'customers' ? { page, size, q } : { page: 1, size: 20 }),
      store.loadFilterLov(auctionId),
    ]);
  },
});

function RouteComponent() {
  const { auctionId } = Route.useParams();
  return <AuctionDetailPage auctionId={auctionId} />;
}
